/**
 * On the task page: the notes, knowledge and documents that belong to this task, so they come up where you already
 * look (Liam: "I tend to forget notes and knowledge, I always refer to tasks"). Link any note by searching for it.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, FileText, Lightbulb, NotebookPen, Plus, X } from "lucide-react";
import { useState } from "react";
import { api, del, post } from "../lib/api";
import { Card } from "./ui";

interface Related {
  notes: Array<{ id: string; title: string; kind: string; snippet: string; how: "meeting" | "linked" | "task" }>;
  knowledge: Array<{ id: string; title: string; snippet: string; team: string | null; mine: boolean; matched: string[] }>;
  documents: Array<{ id: string; title: string; snippet: string | null; path: string | null }>;
}
const HOW = { meeting: "Meeting note · created this task", linked: "Linked note", task: "Note this task came from" } as const;

export function RelatedNotes({ itemId, canLink }: { itemId: string; canLink: boolean }) {
  const qc = useQueryClient();
  const key = ["related", itemId];
  const q = useQuery({ queryKey: key, queryFn: () => api<Related>(`/api/work/items/${itemId}/related`) });
  const [picking, setPicking] = useState(false);
  const [term, setTerm] = useState("");
  const found = useQuery({
    queryKey: ["note-pick", term], enabled: picking && term.trim().length > 1,
    queryFn: () => api<{ items: Array<{ id: string; title: string; snippet: string }> }>(`/api/notes?q=${encodeURIComponent(term.trim())}`),
  });
  const link = useMutation({ mutationFn: (noteId: string) => post(`/api/work/items/${itemId}/notes`, { noteId }), onSuccess: () => { setPicking(false); setTerm(""); void qc.invalidateQueries({ queryKey: key }); } });
  const unlink = useMutation({ mutationFn: (noteId: string) => del(`/api/work/items/${itemId}/notes/${noteId}`), onSuccess: () => void qc.invalidateQueries({ queryKey: key }) });
  const d = q.data;
  const count = d ? d.notes.length + d.knowledge.length + d.documents.length : 0;
  const row = (icon: React.ReactNode, href: string, title: string, sub: string, extra?: React.ReactNode) => (
    <li className="flex items-start gap-2 py-1.5">
      <span className="muted mt-0.5 shrink-0">{icon}</span>
      <a href={href} className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium hover:underline">{title || "Untitled"}</span>
        <span className="muted block truncate text-xs">{sub}</span>
      </a>
      {extra}
    </li>
  );
  return (
    <Card title="Related notes and knowledge" icon={<Lightbulb size={16} />} count={count || undefined}>
      {q.isLoading ? <p className="muted text-sm">Looking…</p> : !count && !picking ? <p className="muted text-sm">Nothing linked or matching yet.</p> : (
        <ul className="divide-y divide-[var(--line)]">
          {d!.notes.map((n) => row(<NotebookPen size={14} />, `/notes?note=${n.id}`, n.title, HOW[n.how] ?? "Note",
            canLink && n.how === "linked" ? <button type="button" className="btn btn-ghost btn-sm !px-1" onClick={() => unlink.mutate(n.id)} aria-label={`Unlink ${n.title}`} title="Unlink"><X size={12} /></button> : undefined))}
          {d!.knowledge.map((k) => row(<BookOpen size={14} />, `/notes?note=${k.id}`, k.title, `Knowledge${k.team ? ` · ${k.team}` : k.mine ? " · your note" : ""}${k.matched.length ? ` · matches “${k.matched.join("”, “")}”` : ""}`,
            canLink ? <button type="button" className="btn btn-ghost btn-sm !px-1" onClick={() => link.mutate(k.id)} title="Link to this task" aria-label={`Link ${k.title}`}><Plus size={12} /></button> : undefined))}
          {d!.documents.map((x) => row(<FileText size={14} />, `/documents/${encodeURIComponent(x.id)}`, x.title, `Document · FusionDocs${x.path ? ` · ${x.path}` : ""}`))}
        </ul>
      )}
      {canLink && (picking ? (
        <div className="mt-2 space-y-1.5">
          <input className="input !py-1 text-sm" autoFocus placeholder="Search your notes…" value={term} onChange={(e) => setTerm(e.target.value)} onKeyDown={(e) => e.key === "Escape" && setPicking(false)} />
          <ul className="max-h-48 overflow-auto">
            {(found.data?.items ?? []).slice(0, 8).map((n) => (
              <li key={n.id}><button type="button" className="w-full truncate rounded px-2 py-1 text-left text-sm hover:bg-black/[0.04]" onClick={() => link.mutate(n.id)}>{n.title || n.snippet || "Untitled"}</button></li>
            ))}
          </ul>
        </div>
      ) : <button type="button" className="btn btn-sm mt-2" onClick={() => setPicking(true)}><Plus size={13} /> Link a note</button>)}
    </Card>
  );
}
